/**
 * HistogramDisplay Component - Main histogram panel with mode toggle
 * Feature: 012-histogram-display
 * User Story 1: View Image Tonal Distribution (MVP)
 */

import { useState, useEffect } from 'react';
import logger from '../../../utils/logger';
import { Application } from 'pixi.js';
import { HistogramMode, ExifData } from '../../../types/histogram';
import { useHistogram } from '../../PhotoEditor/useHistogram';
import { HistogramCanvas } from '../../PhotoEditor/HistogramCanvas';
import { ExifDisplay } from '../../PhotoEditor/ExifDisplay';
import { formatExifForDisplay } from '../../PhotoEditor/histogramUtils';
import { SurfaceProvider } from '@/theme/SurfaceContext';

export interface HistogramDisplayProps {
  /**
   * PixiJS application instance
   */
  app: Application | null;

  /**
   * Image bounds for pixel extraction (excludes canvas background)
   */
  imageBounds?: { x: number; y: number; width: number; height: number } | null;

  /**
   * Trigger histogram recalculation when adjustments change
   */
  adjustmentVersion?: number;

  /**
   * EXIF metadata from photo (feature 003-enhanced-lightbox)
   */
  exifData?: ExifData;
}

/**
 * HistogramDisplay - Main histogram panel component
 *
 * Features:
 * - Real-time histogram calculation from rendered image
 * - Luminance and RGB display modes
 * - Mode toggle button
 * - Loading and error states
 * - Performance monitoring
 *
 * @example
 * <HistogramDisplay
 *   app={pixiApp}
 *   adjustmentVersion={adjustmentCounter}
 * />
 */
export function HistogramDisplay({
  app,
  imageBounds,
  adjustmentVersion,
  exifData,
}: HistogramDisplayProps): JSX.Element {
  const [mode, setMode] = useState<HistogramMode>('luminance');

  const { histogramData, isCalculating, error, recalculate } = useHistogram({
    app,
    imageBounds,
    debounceMs: 50,
    enabled: true,
  });

  // Format EXIF data for display (User Story 3)
  const exifDisplayData = formatExifForDisplay(exifData);

  logger.debug('HistogramDisplay', '[HistogramDisplay] Render state:', {
    hasApp: !!app,
    hasHistogramData: !!histogramData,
    isCalculating,
    error: error?.message,
    exifData: exifData,
    exifDisplayData: exifDisplayData,
    adjustmentVersion,
  });

  /**
   * Recalculate histogram when adjustment version changes
   */
  useEffect(() => {
    if (adjustmentVersion !== undefined) {
      recalculate();
    }
  }, [adjustmentVersion, recalculate]);

  /**
   * Toggle between luminance and RGB modes
   */
  const toggleMode = () => {
    setMode((prevMode) => (prevMode === 'luminance' ? 'rgb' : 'luminance'));
  };

  return (
    <SurfaceProvider level="surface2" theme="light">
      <div className="bg-surface-2 p-4 rounded-lg">
        {/* Header with mode toggle */}
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-foreground">Histogram</h3>

          <button
            onClick={toggleMode}
            className="px-3 py-1 text-xs font-medium bg-surface-1 hover:bg-surface-1/90 text-foreground rounded transition-colors"
            aria-label={`Switch to ${mode === 'luminance' ? 'RGB' : 'luminance'} mode`}
          >
            {mode === 'luminance' ? 'RGB' : 'Lum'}
          </button>
        </div>

        {/* Histogram canvas */}
        <div className="relative">
          {isCalculating && (
            <div className="absolute inset-0 flex items-center justify-center bg-surface-2 bg-opacity-75 z-10">
              <div className="text-xs text-muted">Calculating...</div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-surface-2 bg-opacity-75 z-10">
              <div className="text-xs text-danger">Error: {error.message}</div>
            </div>
          )}

        <HistogramCanvas
          histogramData={histogramData}
          mode={mode}
          width={256}
          height={150}
          className="rounded"
        />
      </div>

        {/* Mode indicator */}
        <div className="mt-2 text-xs text-muted text-center">
          {mode === 'luminance' ? 'Luminance' : 'RGB + Luminance'}
        </div>

        {/* EXIF Metadata Display (User Story 3) */}
        <ExifDisplay exifData={exifDisplayData} />
      </div>
    </SurfaceProvider>
  );
}
